import { Controller } from "@hotwired/stimulus"
import { useTransition, useClickOutside } from "stimulus-use"
import Rails from "@rails/ujs"
import { template, debounce } from "lodash"

export default class extends Controller {
  static targets = ["menu", "input", "list", "resourceTemplate", "blank", "searchQuerySlot", "addWrapper", "outsideClose", "item", "loadingIndicator", "noResults"]

  static values = {
    departmentId: String,
    translations: Object,
    index: { type: Number, default: 0 },
    mode: { type: String, default: "modal" }
  }

  eventCreated = false
  bankAccountData = []
  inputValue = ""
  currentRequest = null
  clickHandler = null

  connect() {
    this.menuTarget.classList.add('hidden')
    this.resourceTemplate = template(this.resourceTemplateTarget.innerHTML)

    useTransition(this, { element: this.menuTarget })
    this.triggerSearch = debounce(this.triggerSearch, 300).bind(this)

    if (this.hasOutsideCloseTarget) {
      useClickOutside(this, {
        element: this.outsideCloseTarget
      })
    }

    this.clickHandler = this.handleClick.bind(this)
    this.inputTarget.addEventListener('click', this.clickHandler)
  }

  open() {
    this.toggle()
  }

  handleClick(_event) {
    if (this.inputTarget.value.trim().length === 0) {
      if (this.bankAccountData.length === 0) {
        this.inputValue = ""
        this.search()
      } else {
        this.showExistingResults()
      }
    }
  }

  showExistingResults() {
    if (this.bankAccountData.length > 0) {
      this.renderItems(this.bankAccountData)
      this.menuTarget.classList.remove("hidden")

      if (this.hasNoResultsTarget) {
        this.noResultsTarget.classList.add("hidden")
      }

      if (this.hasBlankTarget) {
        this.blankTarget.classList.add("hidden")
      }
    }
  }

  triggerSearch(event) {
    const value = event.target.value.trim()

    this.inputValue = value

    const resultsToRemove = this.listTarget.querySelectorAll('li:not([data-dropdown-bank-account-target="loadingIndicator"]):not([data-dropdown-bank-account-target="noResults"])')
    resultsToRemove.forEach(item => item.remove())

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add("hidden")
    }

    if (value.length === 0) {
      this.search()
      return
    }

    if (value.length >= 1) {
      this.search()
    }
  }

  search() {
    if (this.currentRequest) {
      this.currentRequest.abort()
    }

    const alreadyAddedIds = this.getAlreadyAddedBankAccountIds()

    const params = {
      rows: 20,
      name: this.inputValue || "",
      department_id: this.departmentIdValue || "",
      exclude_ids: alreadyAddedIds.join(',')
    }

    this.showLoadingState()
    this.currentRequest = Rails.ajax({
      url: "/find_bank_accounts",
      type: 'get',
      data: new URLSearchParams(params),
      success: (data) => {
        this.hideLoadingState()
        this.showResults(data)
        this.currentRequest = null
      },
      error: (err) => {
        if (err.status !== 0) {
          this.hideLoadingState()

          if (this.hasNoResultsTarget) {
            this.noResultsTarget.classList.remove("hidden")
          }

          this.menuTarget.classList.remove("hidden")
        }
        this.currentRequest = null
      }
    })
  }

  showResults(data) {
    const hasResults = data.length > 0

    if (hasResults) {
      this.renderItems(data)
      if (this.hasNoResultsTarget) {
        this.noResultsTarget.classList.add("hidden")
      }

      this.menuTarget.classList.remove("hidden")

      if (this.modeValue === "inline") {
        this.searchQuerySlotTargets.forEach(el => { el.textContent = this.inputValue })
        this.addWrapperTarget.classList.add("hidden")
      } else {
        if (this.hasBlankTarget) {
          this.blankTarget.classList.add("hidden")
        }
      }
    } else {
      this.hideLoadingState()
      const resultsToRemove = this.listTarget.querySelectorAll('li:not([data-dropdown-bank-account-target="loadingIndicator"]):not([data-dropdown-bank-account-target="noResults"])')
      resultsToRemove.forEach(item => item.remove())

      if (this.hasNoResultsTarget) {
        this.noResultsTarget.classList.remove("hidden")
      }

      this.menuTarget.classList.remove("hidden")

      if (this.hasBlankTarget) {
        this.blankTarget.classList.remove("hidden")
      }
    }
  }

  renderItems(data) {
    this.bankAccountData = data

    this.hideLoadingState()

    const resultsToRemove = this.listTarget.querySelectorAll('li:not([data-dropdown-bank-account-target="loadingIndicator"]):not([data-dropdown-bank-account-target="noResults"])')
    resultsToRemove.forEach(item => item.remove())

    const resultsHtml = data.map((bankAccount, index) => {
      return this.resourceTemplate({
        key: index,
        bankAccount: bankAccount.label,
        name: bankAccount.value
      })
    }).join('')

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.insertAdjacentHTML('afterend', resultsHtml)
    } else {
      this.listTarget.innerHTML = resultsHtml
    }
  }

  // Akcja wisi na liście (`ul`), nie na pozycjach: `li` wstawia renderItems, a Stimulus wiąże
  // `data-action` nowych węzłów dopiero po przetworzeniu mutacji przez MutationObserver — klik
  // w pozycję trafiony w tym okienku przepada bez śladu. Listener na liście istnieje od `connect`,
  // więc indeks czytamy z klikniętej pozycji, a nie z `event.params`.
  select(event) {
    const option = event.target.closest("[data-dropdown-bank-account-value-param]")
    if (!option) return // klik w loader, "brak wyników" albo margines listy

    event.preventDefault()
    const bankAccountIndex = Number(option.dataset.dropdownBankAccountValueParam)
    if (this.modeValue === "inline") {
      this.menuTarget.classList.toggle("hidden", true)
    }

    const selectedAccount = this.bankAccountData[bankAccountIndex]
    this.selectBankAccount(selectedAccount)
    this.bankAccountData = []
    this.hide()
    this.inputTarget.value = ""
    window.dispatchEvent(new CustomEvent("bank-account:selected", { detail: { bank_account: selectedAccount } }))
  }

  selectBankAccount(bankAccount) {
    const bankAccountData = {
      id: bankAccount.id,
      bank_account_id: bankAccount.bank_account_id,
      name: bankAccount.name,
      bank_account_number: bankAccount.bank_account_number,
      bank_name: bankAccount.bank_name,
      bank_swift: bankAccount.bank_swift,
      bank_currency: bankAccount.bank_currency,
      main_on_department: bankAccount.main_on_department || false,
      show_on_invoice: bankAccount.show_on_invoice || false
    }

    const departmentBankAccountSelectController = document.querySelector('[data-controller*="department-bank-account-select"]')
    if (departmentBankAccountSelectController) {
      const controller = this.application.getControllerForElementAndIdentifier(departmentBankAccountSelectController, 'department-bank-account-select')
      if (controller && controller.addBankAccountToExistingList) {
        controller.addBankAccountToExistingList(bankAccountData)
        controller.hide()
      }
    }

    this.dispatch("bankAccountSelected", {
      detail: {
        bankAccount,
        bankAccountId: bankAccount.bank_account_id,
        bankAccountName: bankAccount.name
      }
    })
  }

  toggle() {
    this.toggleTransition()
    if (this.eventCreated === false) {
      this.inputTarget.value = ""
      this.inputTarget.focus()

      if (this.hasOutsideCloseTarget) {
        const fn = this.clickOutside.bind(this)
        this.eventCreated = fn
        document.addEventListener("click", fn)
      }
    } else {
      this.removeEvent()
    }
  }

  hide() {
    this.leave()
    this.removeEvent()
    this.menuTarget.classList.add("hidden")

    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add("hidden")
    }

    const resultsToRemove = this.listTarget.querySelectorAll('li:not([data-dropdown-bank-account-target="loadingIndicator"]):not([data-dropdown-bank-account-target="noResults"])')
    resultsToRemove.forEach(item => item.remove())
  }

  clickOutside() {
    if (!this.menuTarget.classList.contains("hidden")) {
      this.hide()
    }
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      this.eventCreated = false
    }
  }

  disconnect() {
    this.removeEvent()
    if (this.clickHandler) {
      this.inputTarget.removeEventListener('click', this.clickHandler)
    }
  }

  clearSelection() {
    this.inputTarget.value = ''
    this.hide()

    const targetInput = document.querySelector(`[data-bank-account-select-target="input"]`)
    if (targetInput) {
      targetInput.value = ""
      targetInput.dataset.bankAccountId = ""
    }
  }

  clearInput() {
    this.inputTarget.value = ''
    this.hide()
  }

  showLoadingState() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
    this.addWrapperTarget.classList.add('hidden')
    this.menuTarget.classList.remove('hidden')
  }

  hideLoadingState() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
    this.menuTarget.classList.remove('hidden')
  }

  showErrorState() {
    this.addWrapperTarget.classList.add('hidden')
    this.menuTarget.classList.remove('hidden')
  }

  getAlreadyAddedBankAccountIds() {
    const alreadyAddedIds = []

    const hiddenInputs = document.querySelectorAll('input[name="bank_account_version_ids[]"]')
    hiddenInputs.forEach(input => {
      if (input.value && input.value.trim() !== '') {
        alreadyAddedIds.push(input.value.trim())
      }
    })

    return alreadyAddedIds
  }
}
